import { useMemo } from 'react'
import { Modal } from 'antd'
import type { BankInfoIssue } from '@/services/paymentJobs/types'

interface Props {
  open: boolean
  /** モーダル見出し（例: 「給付額算出対象作成を中止しました」） */
  title: string
  /** 概要メッセージ（モーダル冒頭）。 */
  description: string
  issues: BankInfoIssue[]
  onClose: () => void
}

/**
 * 受給者口座情報（FY2603〜FY2607）の不備一覧を表示するモーダル。
 * 給付額算出対象作成 / 振込データ出力の事前チェックで使う。
 */
export default function BankInfoIssueDialog({
  open,
  title,
  description,
  issues,
  onClose,
}: Props) {
  // 同一 identId に複数の不備があるとき、受給者単位でまとめて表示する。
  const grouped = useMemo(() => {
    const map = new Map<string, { identId: string; name: string; rows: BankInfoIssue[] }>()
    for (const issue of issues) {
      const key = `${issue.identId}__${issue.name}`
      const bucket = map.get(key)
      if (bucket) {
        bucket.rows.push(issue)
      } else {
        map.set(key, { identId: issue.identId, name: issue.name, rows: [issue] })
      }
    }
    return Array.from(map.values())
  }, [issues])

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      onOk={onClose}
      okText="閉じる"
      cancelButtonProps={{ style: { display: 'none' } }}
      width={720}
      destroyOnHidden
    >
      <p className="mb-3 text-sm text-gray-700 whitespace-pre-wrap">{description}</p>
      <div className="rounded-lg border border-red-200 bg-red-50">
        <p className="border-b border-red-200 px-3 py-2 text-xs font-medium text-red-700">
          受給者 {grouped.length} 名 / 項目 {issues.length} 件
        </p>
        <ul className="max-h-80 space-y-2 overflow-auto px-3 py-2">
          {grouped.map((group) => (
            <li key={`${group.identId}-${group.name}`} className="rounded border border-red-100 bg-white p-2">
              <p className="text-xs font-semibold text-gray-800">
                <span className="font-mono text-gray-500">{group.identId}</span>
                <span className="mx-2 text-gray-300">|</span>
                {group.name || '—'}
              </p>
              <ul className="mt-1 space-y-0.5">
                {group.rows.map((row, idx) => (
                  <li key={`${row.fieldKey}-${idx}`} className="text-xs text-red-700">
                    ・「{row.label}」: {row.reason}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        受給者管理画面で該当受給者の口座情報を修正してから、もう一度操作してください。
      </p>
    </Modal>
  )
}
